import { useState } from 'react';
import useSWR from 'swr';
import { toast } from 'App';
import { UpdatePermissionGate } from 'components/PermissionGate';
import { PERMISSION_RESOURCES } from 'constants/permissions';
import { adminService } from 'views/services/adminService';
import { feesService } from 'views/services/feesService';
import { IContractFeeRow } from 'views/types/fees';
import {
  cellClass,
  headerClass,
  inputCls,
  primaryBtn,
  secondaryBtn,
  smallBtn,
} from './shared';

interface IDraft {
  deposit_fee: string;
  min_deposit: string;
  withdrawal_fee: string;
  min_withdrawal: string;
}

const EDITABLE_FIELDS: Array<{ key: keyof IDraft; label: string }> = [
  { key: 'deposit_fee', label: 'Deposit fee' },
  { key: 'min_deposit', label: 'Min deposit' },
  { key: 'withdrawal_fee', label: 'Withdrawal fee' },
  { key: 'min_withdrawal', label: 'Min withdrawal' },
];

/**
 * Crypto deposit/withdrawal fee matrix (one row per network x currency).
 * Fees are flat amounts in the token's own units and apply live to the bot.
 * AML fee columns are shown for context but are not editable (client
 * decision 2026-08-06: leave AML fees as they are).
 */
export const ContractFeesPanel = () => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<IDraft | null>(null);
  const [busy, setBusy] = useState(false);

  const { data: rows, mutate } = useSWR('/api/admin/fees/contracts', () =>
    feesService.getContractFees()
  );

  const startEdit = (row: IContractFeeRow) => {
    setEditingId(row.id);
    setDraft({
      deposit_fee: String(row.deposit_fee ?? 0),
      min_deposit: String(row.min_deposit ?? 0),
      withdrawal_fee: String(row.withdrawal_fee ?? 0),
      min_withdrawal: String(row.min_withdrawal ?? 0),
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft(null);
  };

  const save = async () => {
    if (!editingId || !draft) return;
    const patch: Record<string, number> = {};
    for (const { key, label } of EDITABLE_FIELDS) {
      const value = Number(draft[key]);
      if (draft[key].trim() === '' || Number.isNaN(value) || value < 0) {
        toast({
          title: `${label} must be a number ≥ 0`,
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
        return;
      }
      patch[key] = value;
    }
    try {
      setBusy(true);
      await feesService.updateContractFees(editingId, patch);
      toast({ title: 'Fees updated', status: 'success' });
      cancelEdit();
      await mutate();
    } catch (error) {
      adminService.errorHandler(error);
    } finally {
      setBusy(false);
    }
  };

  const editableCell = (key: keyof IDraft, row: IContractFeeRow) =>
    editingId === row.id && draft ? (
      <input
        className={`${inputCls} min-w-[90px]`}
        value={draft[key]}
        onChange={e => setDraft({ ...draft, [key]: e.target.value })}
      />
    ) : (
      row[key]
    );

  return (
    <div>
      <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
        Flat amounts in the token&apos;s own units (e.g. 10 = 10 USDT). Changes
        apply live to the bot &mdash; no redeploy needed. AML fees are shown for
        reference and cannot be edited.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr>
              <th className={headerClass}>Network</th>
              <th className={headerClass}>Currency</th>
              <th className={headerClass}>Deposit fee</th>
              <th className={headerClass}>AML (deposit)</th>
              <th className={headerClass}>Min deposit</th>
              <th className={headerClass}>Withdrawal fee</th>
              <th className={headerClass}>AML (withdrawal)</th>
              <th className={headerClass}>Min withdrawal</th>
              <th className={headerClass}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows?.map(row => (
              <tr key={row.id}>
                <td className={cellClass}>{row.network}</td>
                <td className={cellClass}>{row.currency}</td>
                <td className={cellClass}>
                  {editableCell('deposit_fee', row)}
                </td>
                <td className={`${cellClass} text-gray-400`}>
                  {row.deposit_aml_fee}
                </td>
                <td className={cellClass}>
                  {editableCell('min_deposit', row)}
                </td>
                <td className={cellClass}>
                  {editableCell('withdrawal_fee', row)}
                </td>
                <td className={`${cellClass} text-gray-400`}>
                  {row.withdrawal_aml_fee}
                </td>
                <td className={cellClass}>
                  {editableCell('min_withdrawal', row)}
                </td>
                <td className={`${cellClass} flex gap-2`}>
                  <UpdatePermissionGate resource={PERMISSION_RESOURCES.FEES}>
                    {editingId === row.id ? (
                      <>
                        <button
                          className={primaryBtn}
                          disabled={busy}
                          onClick={save}
                        >
                          Save
                        </button>
                        <button
                          className={secondaryBtn}
                          disabled={busy}
                          onClick={cancelEdit}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        className={smallBtn}
                        onClick={() => startEdit(row)}
                      >
                        Edit
                      </button>
                    )}
                  </UpdatePermissionGate>
                </td>
              </tr>
            ))}
            {rows && rows.length === 0 && (
              <tr>
                <td
                  className="py-4 text-center text-sm text-gray-500"
                  colSpan={9}
                >
                  No contracts found.
                </td>
              </tr>
            )}
            {!rows && (
              <tr>
                <td
                  className="py-4 text-center text-sm text-gray-500"
                  colSpan={9}
                >
                  Loading…
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
